import { toast } from "sonner";
import { Button } from "@/components/catalyst/button";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { translateBlogPostMutation } from "@/app/Blogs/GraphQL/blogPostMutations";
import type { BlogPostRecord } from "@/app/Blogs/GraphQL/blogPostRecordQuery";
import { ExternalLinkIcon, RefreshCwIcon } from "lucide-react";

type Translation = BlogPostRecord["translations"][number];

type Props = {
  post: BlogPostRecord;
  locale: { code: string; label: string };
  translation: Translation | undefined;
  isSource: boolean;
};

export function BlogTranslationLocaleRow({
  post,
  locale,
  translation,
  isSource,
}: Props) {
  const { mutateAsync: translate, isPending } = useGqlMutation(
    translateBlogPostMutation,
  );

  const appUrl = import.meta.env.VITE_APP_URL as string | undefined;
  const url = appUrl ? `${appUrl}/${locale.code}/blog/${post.slug}` : null;
  const showUrl = (isSource || translation) && url;

  return (
    <li className="flex items-start justify-between gap-4 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-900">
            {locale.label}
          </span>
          <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600">
            {locale.code}
          </span>
          {isSource ? (
            <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
              Source
            </span>
          ) : translation ? (
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
              Translated
            </span>
          ) : (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700">
              Missing
            </span>
          )}
        </div>
        {isSource ? (
          <p className="mt-1 truncate text-sm text-zinc-700">{post.title}</p>
        ) : translation ? (
          <p className="mt-1 truncate text-sm text-zinc-700">
            {translation.title ?? "—"}
          </p>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {showUrl ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 rounded-md border border-zinc-200 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50"
          >
            <ExternalLinkIcon className="size-3" />
            View
          </a>
        ) : null}
        {!isSource ? (
          <Button
            outline
            LeadingIcon={RefreshCwIcon}
            disabled={isPending}
            isLoading={isPending}
            onClick={async () => {
              await translate({ id: post.id, locale: locale.code });
              toast.success(`${locale.label} translation started`);
            }}
          >
            {translation ? "Regenerate" : "Translate"}
          </Button>
        ) : null}
      </div>
    </li>
  );
}
